import * as React from 'react';
import { Sparkline } from '../charts/sparkline';
import { cn } from '../../lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface TrendSparklineProps {
  /** Data points (most recent last). */
  data: number[];
  /** Optional setpoint for delta-vs-setpoint coloring. */
  setpoint?: number;
  /** Live = pulse on the latest dot. */
  live?: boolean;
  /** Compact mode hides label + delta. */
  compact?: boolean;
  /** Eyebrow (e.g. tag id). */
  label?: React.ReactNode;
  /** Format the value (e.g. "°C"). */
  unit?: string;
  className?: string;
}

/**
 * Tyndo TrendSparkline — sparkline + delta indicator.
 *
 * Builds on the base Sparkline. Adds a numeric value display, a delta-vs-prev
 * indicator (or vs-setpoint when provided), and direction icon. Drop into KPI
 * cards, table cells, and historian preview rows.
 */
export function TrendSparkline({
  data,
  setpoint,
  live,
  compact,
  label,
  unit,
  className,
}: TrendSparklineProps) {
  if (data.length === 0) {
    return (
      <span className={cn('inline-flex items-center text-xs text-muted-foreground/50', className)}>
        No data
      </span>
    );
  }

  const latest = data[data.length - 1] ?? 0;
  const prev = (data.length > 1 ? data[data.length - 2] : latest) ?? latest;
  const refPoint = setpoint ?? prev;
  const delta = latest - refPoint;
  const deltaPct = Math.abs(refPoint) > 1e-9 ? (delta / refPoint) * 100 : 0;

  const direction: 'up' | 'down' | 'flat' =
    Math.abs(delta) < 1e-6 ? 'flat' : delta > 0 ? 'up' : 'down';

  // Tone — when comparing to setpoint, "off" is bad; when comparing to prev,
  // direction is informational only.
  const offSetpoint = setpoint !== undefined && Math.abs(deltaPct) > 5;
  const sparkColor: 'phosphor' | 'alarm-major' | 'state-running' = offSetpoint
    ? 'alarm-major'
    : 'phosphor';

  const TrendIcon =
    direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;

  return (
    <div className={cn('inline-flex items-center gap-2.5', className)}>
      {!compact && (
        <div className="text-right">
          {label && <p className="t-eyebrow">{label}</p>}
          <p className="t-tabular tabular-nums text-lg font-semibold leading-tight text-foreground">
            {Math.round(latest * 100) / 100}
            {unit && <span className="ml-0.5 text-[10px] text-muted-foreground font-normal">{unit}</span>}
          </p>
          {setpoint !== undefined && (
            <p
              className="t-mono text-[10px]"
              style={{
                color: offSetpoint
                  ? 'hsl(var(--alarm-major))'
                  : 'hsl(var(--accent-live))',
              }}
            >
              <TrendIcon className="inline h-2.5 w-2.5" />
              {' '}
              {delta >= 0 ? '+' : ''}
              {Math.round(deltaPct * 10) / 10}% vs SP
            </p>
          )}
        </div>
      )}
      <Sparkline
        data={data}
        setpoint={setpoint}
        color={sparkColor}
        live={live}
        width={compact ? 60 : 88}
        height={compact ? 18 : 32}
      />
    </div>
  );
}
